import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import { AccessSecretDialog } from "components/common/AccessSecretDialog";
import { Breadcrumbs } from "components/common/Breadcrumbs";
import { Breadcrumb } from "components/common/Breadcrumbs/types";
import { FormDialog } from "components/common/FormDialog";
import {
  FIELD_TYPES,
  FormDialogProps
} from "components/common/FormDialog/types";
import { Head } from "components/common/Head";
import { Loader } from "components/common/Loader";
import { Table } from "components/common/Table";
import {
  TableColumn,
  TableRowActionsMenuItem
} from "components/common/Table/types";
import { TableCollapsible } from "components/common/TableCollapsible";
import { useTour } from "components/common/Tour";
import {
  CollapsibleData,
  TableColumn as TableCollapsibleColumn,
  TableCollapsibleRowActionsMenuItem,
  TableRowActionsMenuItem as TableCollapsibleMainRowActionsMenuItem
} from "components/common/TableCollapsible/types";
import copy from "copy-to-clipboard";
import { useMount } from "hooks/useMount";
import { usePrevious } from "hooks/usePrevious";
import { useUnmount } from "hooks/useUnmount";
import { enterprisesActions } from "modules/enterprises/slices";
import { organizationSelector } from "modules/enterprises/selectors";
import { objectStorageActions } from "modules/objectStorage/slices";
import {
  areCredentialsCreatingSelector,
  areCredentialsDeletingSelector,
  areCredentialsRegeneratingSelector,
  credentialsSelector,
  isSubUserCreatingSelector,
  isSubUserDeletingSelector,
  isSubUserKeyCreatingSelector,
  isSubUserKeyDeletingSelector,
  subUserKeySelector,
  tableCredentialsListSelector,
  tableSubUsersListSelector
} from "modules/objectStorage/selectors";
import { TableCredentials, TableSubUser } from "modules/objectStorage/types";
import { notificationsActions } from "modules/notifications/slices";
import { NOTIFICATION_TYPES } from "modules/notifications/types";
import { pollingActions } from "modules/polling/slices";
import { createPolling } from "modules/polling/utils";
import { projectsActions } from "modules/projects/slices";
import { projectSelector } from "modules/projects/selectors";
import { S3Buckets } from "components/S3Buckets";
import { FC, Fragment, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams
} from "react-router-dom";
import { isString } from "typeGuards/isString";
import { generateSearchString } from "utils/generateSearchString";
import { string } from "yup";
import { ROUTES } from "../../constants";
import * as s from "./styles";
import {
  createCredentialsTour,
  credentialsTour,
  objectStorageTour
} from "./tour";
import { DIALOG_TYPES, TABS, TOUR_ANCHORS } from "./types";

const POLL_ID_PREFIX = "OBJECT_STORAGE";

const POLL_IDS = {
  credentialsList: "CREDENTIALS_LIST",
  subUsersList: "SUB_USERS_LIST"
};

// the endpoint is the same for every row, so it lives in the page header
const credentialsTableColumns: TableColumn<TableCredentials>[] = [
  { key: "accessKey", label: "Access key" },
  { key: "owner", label: "Owner" }
];

const subUsersTableColumns: TableCollapsibleColumn<TableSubUser>[] = [
  { key: "name", label: "Subuser name", width: "25%" },
  { key: "principalArn", label: "Principal ARN", maxLength: 43 },
  { key: "access", label: "Access", width: "160px" },
  { key: "keysCount", label: "Keys", width: "80px" }
];

const subUserKeysTableColumns: TableCollapsibleColumn<CollapsibleData>[] = [
  { key: "accessKey", label: "Access key" }
];

const SUB_USER_ACCESS_OPTIONS = [
  { label: "None", value: "none" },
  { label: "Read", value: "read" },
  { label: "Write", value: "write" },
  { label: "Read & write", value: "readwrite" },
  { label: "Full", value: "full" }
];

// shown under the buttons, for the level that is picked at the moment
const SUB_USER_ACCESS_DESCRIPTIONS: { [access: string]: string } = {
  none: "No access by default. Use bucket policies to grant access to specific buckets.",
  read: "Can list buckets, view and download objects. Cannot upload, modify or delete them.",
  write:
    "Can upload and delete objects. Cannot read or download them by default.",
  readwrite:
    "Can view, download, upload and delete objects. Cannot manage bucket policies.",
  full: "Can read and write objects, plus manage bucket settings and policies."
};

const title = "Object Storage";

const TAB_TITLES: { [key in TABS]: string } = {
  [TABS.CREDENTIALS]: "Credentials",
  [TABS.BROWSER]: "Browser"
};

export const ObjectStorage: FC = () => {
  const dispatch = useDispatch();
  const matchParams = useParams<{
    organizationId: string;
    regionId: string;
    projectId: string;
  }>();
  const history = useNavigate();
  const { startTour } = useTour();
  const location = useLocation();
  const organization = useSelector(organizationSelector);
  const project = useSelector(projectSelector);
  const credentials = useSelector(credentialsSelector);
  const previousCredentials = usePrevious(credentials);
  const credentialsList = useSelector(tableCredentialsListSelector);
  const subUsersList = useSelector(tableSubUsersListSelector);
  const subUserKey = useSelector(subUserKeySelector);
  const previousSubUserKey = usePrevious(subUserKey);
  // The project has a single set of S3 credentials, the same one the
  // "Browser" tab uses. Subusers belong to it.
  const credsId = credentialsList?.[0]?.id;
  const endpoint = credentialsList?.[0]?.endpoint;
  const principalArn = `arn:aws:iam::${matchParams.projectId}:user/${matchParams.projectId}`;
  const activeTabIndexFromParam = Object.keys(TAB_TITLES).find(
    (key) =>
      TAB_TITLES[key] &&
      String(TAB_TITLES[key]).toLowerCase() ===
        new URLSearchParams(location.search).get("tab")
  );
  const [activeTabIndex, setActiveTabIndex] = useState<TABS>(
    Number(activeTabIndexFromParam || TABS.CREDENTIALS)
  );
  // the browser tab needs credentials, so it falls back to the credentials
  // tab only once the list has loaded and turned out to be empty
  const visibleTabIndex =
    !credentialsList || credsId ? activeTabIndex : TABS.CREDENTIALS;
  const handleChangeTab = useCallback((e, value: number) => {
    setActiveTabIndex(value);
  }, []);

  const handleCopyToClipboard = useCallback(
    (text: string, name: string) => {
      copy(text);
      dispatch(
        notificationsActions.showNotification({
          type: NOTIFICATION_TYPES.INFO,
          title: `${name} has been copied to clipboard.`
        })
      );
    },
    [dispatch]
  );

  const [dialog, setDialog] = useState<{
    isOpened: boolean;
    type: DIALOG_TYPES;
  }>({ type: DIALOG_TYPES.CREATE_CREDENTIALS, isOpened: false });
  const [isAccessSecretDialogOpened, setIsAccessSecretDialogOpened] =
    useState(false);
  const [isSubUserKeySecretDialogOpened, setIsSubUserKeySecretDialogOpened] =
    useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  // A subuser is identified by its name, a key by its access key. A key is
  // only unique inside its subuser, so both ids are kept for key actions.
  const [selectedSubUserId, setSelectedSubUserId] = useState<string | null>(
    null
  );
  const [selectedSubUserKeyId, setSelectedSubUserKeyId] = useState<
    string | null
  >(null);

  const previousSelectedSubUserId = usePrevious(selectedSubUserId);
  const selectedSubUserName = selectedSubUserId
    ? selectedSubUserId
    : previousSelectedSubUserId;

  const previousSelectedSubUserKeyId = usePrevious(selectedSubUserKeyId);
  const selectedSubUserKeyName = selectedSubUserKeyId
    ? selectedSubUserKeyId
    : previousSelectedSubUserKeyId;

  const areCredentialsCreating = useSelector(areCredentialsCreatingSelector);
  const previousAreCredentialsCreating = usePrevious(areCredentialsCreating);
  const areCredentialsRegenerating = useSelector(
    areCredentialsRegeneratingSelector
  );
  const areCredentialsDeleting = useSelector(areCredentialsDeletingSelector);
  const isCredentialsOperationInProgress =
    areCredentialsCreating ||
    areCredentialsRegenerating ||
    areCredentialsDeleting;
  const previousIsCredentialsOperationInProgress = usePrevious(
    isCredentialsOperationInProgress
  );
  const isSubUserCreating = useSelector(isSubUserCreatingSelector);
  const isSubUserDeleting = useSelector(isSubUserDeletingSelector);
  const isSubUserKeyCreating = useSelector(isSubUserKeyCreatingSelector);
  const isSubUserKeyDeleting = useSelector(isSubUserKeyDeletingSelector);
  const isSubUserOperationInProgress =
    isSubUserCreating ||
    isSubUserDeleting ||
    isSubUserKeyCreating ||
    isSubUserKeyDeleting;
  const previousIsSubUserOperationInProgress = usePrevious(
    isSubUserOperationInProgress
  );

  const handleCloseDialog = useCallback(() => {
    setDialog({
      ...dialog,
      isOpened: false
    });
    setSelectedItemId(null);
    setSelectedSubUserId(null);
    setSelectedSubUserKeyId(null);
  }, [dialog]);

  const handleCreateCredentialsButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.CREATE_CREDENTIALS,
      isOpened: true
    });
  }, []);

  const handleShowCredentialsMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.SHOW_CREDENTIALS,
      isOpened: true
    });
  }, []);

  const handleRegenerateCredentialsMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.REGENERATE_CREDENTIALS,
      isOpened: true
    });
  }, []);

  const handleDeleteCredentialsMenuItemClick = useCallback((id: string) => {
    setSelectedItemId(id);
    setDialog({
      type: DIALOG_TYPES.DELETE_CREDENTIALS,
      isOpened: true
    });
  }, []);

  // the tour explains the keys, so it runs on the credentials tab
  // set when the tour ended on creating the credentials, and the rest of it
  // waits for them to appear
  const [isTourWaitingForCredentials, setIsTourWaitingForCredentials] =
    useState(false);

  const handleTourButtonClick = useCallback(() => {
    setActiveTabIndex(TABS.CREDENTIALS);
    if (credsId) {
      startTour(objectStorageTour);
      return;
    }
    startTour(createCredentialsTour, {
      onFinish: () => setIsTourWaitingForCredentials(true)
    });
  }, [credsId, startTour]);

  // carries on once the new credentials are in the table and the window with
  // their secret is closed (closing it clears `credentials`)
  useEffect(() => {
    if (
      isTourWaitingForCredentials &&
      credsId &&
      !credentials &&
      !isAccessSecretDialogOpened
    ) {
      setIsTourWaitingForCredentials(false);
      startTour(credentialsTour);
    }
  }, [
    isTourWaitingForCredentials,
    credsId,
    credentials,
    isAccessSecretDialogOpened,
    startTour
  ]);

  const handleCreateSubUserButtonClick = useCallback(() => {
    setDialog({
      type: DIALOG_TYPES.CREATE_SUB_USER,
      isOpened: true
    });
  }, []);

  const handleDeleteSubUserMenuItemClick = useCallback((id: string) => {
    setSelectedSubUserId(id);
    setDialog({
      type: DIALOG_TYPES.DELETE_SUB_USER,
      isOpened: true
    });
  }, []);

  const handleCreateSubUserKeyMenuItemClick = useCallback((id: string) => {
    setSelectedSubUserId(id);
    setDialog({
      type: DIALOG_TYPES.CREATE_SUB_USER_KEY,
      isOpened: true
    });
  }, []);

  const handleShowSubUserKeyMenuItemClick = useCallback(
    (id: string, parentId: string) => {
      setSelectedSubUserId(parentId);
      setSelectedSubUserKeyId(id);
      setDialog({
        type: DIALOG_TYPES.SHOW_SUB_USER_KEY_SECRET,
        isOpened: true
      });
    },
    []
  );

  const handleDeleteSubUserKeyMenuItemClick = useCallback(
    (id: string, parentId: string) => {
      setSelectedSubUserId(parentId);
      setSelectedSubUserKeyId(id);
      setDialog({
        type: DIALOG_TYPES.DELETE_SUB_USER_KEY,
        isOpened: true
      });
    },
    []
  );

  const credentialsTableActions: TableRowActionsMenuItem<TableCredentials>[] = [
    {
      label: "Show access secret",
      handler: handleShowCredentialsMenuItemClick
    },
    {
      label: "Re-generate secret",
      handler: handleRegenerateCredentialsMenuItemClick
    },
    {
      label: "Delete",
      handler: handleDeleteCredentialsMenuItemClick
    }
  ];

  const subUsersTableActions: TableCollapsibleMainRowActionsMenuItem<TableSubUser>[] =
    [
      {
        label: "Add key",
        handler: handleCreateSubUserKeyMenuItemClick
      },
      {
        label: "Delete subuser",
        handler: handleDeleteSubUserMenuItemClick
      }
    ];

  const subUserKeysTableActions: TableCollapsibleRowActionsMenuItem[] = [
    {
      label: "Show access secret",
      handler: handleShowSubUserKeyMenuItemClick
    },
    {
      label: "Delete key",
      handler: handleDeleteSubUserKeyMenuItemClick
    }
  ];

  const breadcrumbs: Breadcrumb[] = [
    { text: "Organizations", url: ROUTES.ORGANIZATIONS },
    {
      text: organization?.name || "",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: "Projects",
      url: generatePath(ROUTES.ORGANIZATION, {
        organizationId: matchParams.organizationId
      })
    },
    {
      text: project?.name || "",
      url: generatePath(ROUTES.PROJECT, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    },
    {
      text: title,
      url: generatePath(ROUTES.OBJECT_STORAGE, {
        organizationId: matchParams.organizationId,
        regionId: matchParams.regionId,
        projectId: matchParams.projectId
      })
    }
  ];

  useMount(() => {
    dispatch(
      projectsActions.getProjectRequest({
        regionId: matchParams.regionId!,
        id: matchParams.projectId!
      })
    );
    dispatch(
      enterprisesActions.getOrganizationRequest({
        id: matchParams.organizationId!
      })
    );
    dispatch(
      enterprisesActions.getOrganizationRequest({
        id: matchParams.organizationId!
      })
    );
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.credentialsList}`,
        actionCreator: objectStorageActions.getCredentialsListRequest,
        params: {
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!
        }
      })
    );
  });

  useUnmount(() => {
    Object.values(POLL_IDS).forEach((id) => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${id}`
        })
      );
    });
    dispatch(enterprisesActions.clear());
    dispatch(projectsActions.clear());
    dispatch(objectStorageActions.clear());
  });

  useEffect(() => {
    history({
      search: generateSearchString({
        tab: TAB_TITLES[activeTabIndex]
      })
    });
  }, [activeTabIndex, history]);

  // e.g. the only credentials were deleted while the browser tab was open
  useEffect(() => {
    if (credentialsList && !credsId) {
      setActiveTabIndex(TABS.CREDENTIALS);
    }
  }, [credentialsList, credsId]);

  useEffect(() => {
    if (
      previousIsCredentialsOperationInProgress &&
      !isCredentialsOperationInProgress
    ) {
      dispatch(
        objectStorageActions.getCredentialsListRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!
        })
      );
    }
  }, [
    previousIsCredentialsOperationInProgress,
    isCredentialsOperationInProgress,
    dispatch,
    matchParams.projectId,
    matchParams.regionId
  ]);

  // this action is necessary to initialize the federated user in rgw's oidc namespace
  // `credentials` holds the just created ones once the request has succeeded
  useEffect(() => {
    if (
      previousAreCredentialsCreating &&
      !areCredentialsCreating &&
      credentials
    ) {
      dispatch(
        objectStorageActions.getBucketsRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId: credentials.id
        })
      );
    }
  }, [
    previousAreCredentialsCreating,
    areCredentialsCreating,
    credentials,
    dispatch,
    matchParams.projectId,
    matchParams.regionId
  ]);

  useEffect(() => {
    if (
      previousIsSubUserOperationInProgress &&
      !isSubUserOperationInProgress &&
      credsId
    ) {
      dispatch(
        objectStorageActions.getSubUsersRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId
        })
      );
    }
  }, [
    previousIsSubUserOperationInProgress,
    isSubUserOperationInProgress,
    credsId,
    dispatch,
    matchParams.projectId,
    matchParams.regionId
  ]);

  // fetch buckets in advance so the browser tab opens without a loader
  useEffect(() => {
    if (!credsId) {
      return;
    }
    dispatch(
      objectStorageActions.getBucketsRequest({
        regionId: matchParams.regionId!,
        projId: matchParams.projectId!,
        credsId
      })
    );
  }, [credsId, dispatch, matchParams.projectId, matchParams.regionId]);

  /*
    Subusers hang off the credentials, so their poll can only start once the
    credentials list has arrived and `credsId` is known.
  */
  useEffect(() => {
    if (!credsId || visibleTabIndex !== TABS.CREDENTIALS) {
      return;
    }
    dispatch(
      createPolling({
        id: `${POLL_ID_PREFIX}/${POLL_IDS.subUsersList}`,
        actionCreator: objectStorageActions.getSubUsersRequest,
        params: {
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId
        }
      })
    );
    return () => {
      dispatch(
        pollingActions.stopPolling({
          id: `${POLL_ID_PREFIX}/${POLL_IDS.subUsersList}`
        })
      );
    };
  }, [
    credsId,
    visibleTabIndex,
    dispatch,
    matchParams.projectId,
    matchParams.regionId
  ]);

  useEffect(() => {
    if (previousCredentials !== credentials && credentials) {
      setIsAccessSecretDialogOpened(true);
    }
  }, [previousCredentials, credentials]);

  useEffect(() => {
    if (previousSubUserKey !== subUserKey && subUserKey) {
      setIsSubUserKeySecretDialogOpened(true);
    }
  }, [previousSubUserKey, subUserKey]);

  const handleConfirmShowCredentials = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        objectStorageActions.getCredentialsRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId: selectedItemId
        })
      );
    }
    handleCloseDialog();
  }, [
    dispatch,
    selectedItemId,
    matchParams.projectId,
    handleCloseDialog,
    matchParams.regionId
  ]);

  const handleConfirmCreateCredentials = useCallback(() => {
    dispatch(
      objectStorageActions.createCredentialsRequest({
        regionId: matchParams.regionId!,
        projId: matchParams.projectId!
      })
    );
    handleCloseDialog();
  }, [
    dispatch,
    matchParams.projectId,
    handleCloseDialog,
    matchParams.regionId
  ]);

  const handleConfirmReGenerateCredentials = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        objectStorageActions.regenerateCredentialsRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId: selectedItemId
        })
      );
    }
    handleCloseDialog();
  }, [
    dispatch,
    selectedItemId,
    matchParams.projectId,
    handleCloseDialog,
    matchParams.regionId
  ]);

  const handleConfirmDeleteCredentials = useCallback(() => {
    if (selectedItemId) {
      dispatch(
        objectStorageActions.deleteCredentialsRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId: selectedItemId
        })
      );
    }
    handleCloseDialog();
  }, [
    dispatch,
    selectedItemId,
    matchParams.projectId,
    handleCloseDialog,
    matchParams.regionId
  ]);

  const handleConfirmCreateSubUser = useCallback(
    (data: { name: string; access: string }) => {
      if (credsId) {
        dispatch(
          objectStorageActions.createSubUserRequest({
            regionId: matchParams.regionId!,
            projId: matchParams.projectId!,
            credsId,
            data: {
              name: data.name,
              // API expects an empty string for no access, not "none"
              access: data.access === "none" ? "" : data.access
            }
          })
        );
      }
      handleCloseDialog();
    },
    [
      credsId,
      dispatch,
      handleCloseDialog,
      matchParams.projectId,
      matchParams.regionId
    ]
  );

  const handleConfirmDeleteSubUser = useCallback(() => {
    if (credsId && selectedSubUserId) {
      dispatch(
        objectStorageActions.deleteSubUserRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId,
          subUserName: selectedSubUserId
        })
      );
    }
    handleCloseDialog();
  }, [
    credsId,
    selectedSubUserId,
    dispatch,
    handleCloseDialog,
    matchParams.projectId,
    matchParams.regionId
  ]);

  const handleConfirmCreateSubUserKey = useCallback(() => {
    if (credsId && selectedSubUserId) {
      dispatch(
        objectStorageActions.createSubUserKeyRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId,
          subUserName: selectedSubUserId
        })
      );
    }
    handleCloseDialog();
  }, [
    credsId,
    selectedSubUserId,
    dispatch,
    handleCloseDialog,
    matchParams.projectId,
    matchParams.regionId
  ]);

  const handleConfirmShowSubUserKeySecret = useCallback(() => {
    if (credsId && selectedSubUserId && selectedSubUserKeyId) {
      dispatch(
        objectStorageActions.getSubUserKeyRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId,
          subUserName: selectedSubUserId,
          accessKey: selectedSubUserKeyId
        })
      );
    }
    handleCloseDialog();
  }, [
    credsId,
    selectedSubUserId,
    selectedSubUserKeyId,
    dispatch,
    handleCloseDialog,
    matchParams.projectId,
    matchParams.regionId
  ]);

  const handleConfirmDeleteSubUserKey = useCallback(() => {
    if (credsId && selectedSubUserId && selectedSubUserKeyId) {
      dispatch(
        objectStorageActions.deleteSubUserKeyRequest({
          regionId: matchParams.regionId!,
          projId: matchParams.projectId!,
          credsId,
          subUserName: selectedSubUserId,
          accessKey: selectedSubUserKeyId
        })
      );
    }
    handleCloseDialog();
  }, [
    credsId,
    selectedSubUserId,
    selectedSubUserKeyId,
    dispatch,
    handleCloseDialog,
    matchParams.projectId,
    matchParams.regionId
  ]);

  const dialogProps: {
    [key in DIALOG_TYPES]: Omit<FormDialogProps, "isOpened" | "onCancel">;
  } = {
    [DIALOG_TYPES.SHOW_CREDENTIALS]: {
      onConfirm: handleConfirmShowCredentials,
      title: "Are you sure you want to show access secret?",
      confirmButtonLabel: "Show"
    },
    [DIALOG_TYPES.CREATE_CREDENTIALS]: {
      onConfirm: handleConfirmCreateCredentials,
      title: "Are you sure you want to create a new master key?",
      confirmButtonLabel: "Create"
    },
    [DIALOG_TYPES.REGENERATE_CREDENTIALS]: {
      onConfirm: handleConfirmReGenerateCredentials,
      title: "Are you sure you want to re-generate access secret?",
      confirmButtonLabel: "Re-generate secret"
    },
    [DIALOG_TYPES.DELETE_CREDENTIALS]: {
      onConfirm: handleConfirmDeleteCredentials,
      title: `Are you sure you want to delete the master key?`,
      fields: [
        {
          name: "lastMasterKeyWarning",
          type: FIELD_TYPES.NOTES,
          label:
            "ℹ️  The project should have at least one master key.\n\n⚠️ If you need to delete the last one, make sure all subusers and buckets are deleted first."
        }
      ],
      confirmButtonLabel: "Delete"
    },
    [DIALOG_TYPES.CREATE_SUB_USER]: {
      onConfirm: handleConfirmCreateSubUser,
      title: "Create subuser",
      confirmButtonLabel: "Create",
      fields: [
        {
          name: "name",
          type: FIELD_TYPES.TEXT,
          label: "Name",
          rules: string()
            .required()
            .test("sameNameCheckFormat", "This name already exists", (value) =>
              Boolean(
                value && !subUsersList?.some((user) => user.name === value)
              )
            )
        },
        {
          name: "access",
          type: FIELD_TYPES.TOGGLE_BUTTON,
          label: "Access",
          helperText: "Select the access level for this subuser.",
          options: SUB_USER_ACCESS_OPTIONS,
          defaultValue: "read",
          rules: string().required()
        },
        {
          name: "accessDescription",
          type: FIELD_TYPES.NOTES,
          label: (fieldValues) => {
            const description =
              SUB_USER_ACCESS_DESCRIPTIONS[String(fieldValues.access)];
            return description
              ? `\n\n\u2139\uFE0F\u00A0\u00A0${description}`
              : "";
          }
        }
      ]
    },
    [DIALOG_TYPES.DELETE_SUB_USER]: {
      onConfirm: handleConfirmDeleteSubUser,
      title: `Are you sure you want to delete subuser "${
        selectedSubUserName ?? "selected"
      }"?`,
      fields: [
        {
          name: "subUserKeysWarning",
          type: FIELD_TYPES.NOTES,
          label: "⚠️ All keys of this subuser will be deleted as well."
        }
      ],
      confirmButtonLabel: "Delete"
    },
    [DIALOG_TYPES.CREATE_SUB_USER_KEY]: {
      onConfirm: handleConfirmCreateSubUserKey,
      title: `Add a key for subuser "${selectedSubUserName ?? "selected"}"?`,
      confirmButtonLabel: "Add"
    },
    [DIALOG_TYPES.SHOW_SUB_USER_KEY_SECRET]: {
      onConfirm: handleConfirmShowSubUserKeySecret,
      title: "Are you sure you want to show access secret?",
      confirmButtonLabel: "Show"
    },
    [DIALOG_TYPES.DELETE_SUB_USER_KEY]: {
      onConfirm: handleConfirmDeleteSubUserKey,
      title: `Are you sure you want to delete key "${
        selectedSubUserKeyName ?? "selected"
      }"?`,
      confirmButtonLabel: "Delete"
    }
  };

  const handleCloseAccessSecretDialog = useCallback(() => {
    setIsAccessSecretDialogOpened(false);
    dispatch(objectStorageActions.clearCredentials());
  }, [dispatch]);

  const handleCloseSubUserKeySecretDialog = useCallback(() => {
    setIsSubUserKeySecretDialogOpened(false);
    dispatch(objectStorageActions.clearSubUserKey());
  }, [dispatch]);

  const tabs = (
    <Tabs value={visibleTabIndex} onChange={handleChangeTab}>
      <Tab
        label={TAB_TITLES[TABS.CREDENTIALS]}
        data-tour={TOUR_ANCHORS.CREDENTIALS_TAB}
      />
      <Tab
        label={TAB_TITLES[TABS.BROWSER]}
        disabled={!credsId}
        data-tour={TOUR_ANCHORS.BROWSER_TAB}
      />
    </Tabs>
  );

  return (
    <>
      <Head title={title} />
      {organization && project && <Breadcrumbs breadcrumbs={breadcrumbs} />}
      <s.SummaryContainer>
        <s.SummaryColumn>
          <s.Title title={title} variant={"h4"} component={"h2"}>
            {title}
          </s.Title>
          {endpoint && (
            <>
              <s.SummaryRow data-tour={TOUR_ANCHORS.PRINCIPAL_ARN}>
                <s.DetailsTitle>Principal ARN: </s.DetailsTitle>
                <Tooltip title={principalArn} placement={"top"} arrow>
                  <s.Tag
                    label={`${principalArn.slice(0, 33)}...`}
                    deleteIcon={<ContentCopyIcon />}
                    onDelete={() =>
                      handleCopyToClipboard(principalArn, "Principal ARN")
                    }
                  />
                </Tooltip>
              </s.SummaryRow>
              <s.SummaryRow data-tour={TOUR_ANCHORS.ENDPOINT}>
                <s.DetailsTitle>Endpoint: </s.DetailsTitle>
                <s.Tag
                  label={endpoint}
                  deleteIcon={<ContentCopyIcon />}
                  onDelete={() => handleCopyToClipboard(endpoint, "Endpoint")}
                />
              </s.SummaryRow>
            </>
          )}
        </s.SummaryColumn>
        <s.ActionsContainer>
          <Tooltip title={"Take a tour of this page"} placement={"top"} arrow>
            <IconButton onClick={handleTourButtonClick} color={"inherit"}>
              <AutoAwesomeOutlinedIcon />
            </IconButton>
          </Tooltip>
        </s.ActionsContainer>
      </s.SummaryContainer>
      {visibleTabIndex === TABS.BROWSER ? (
        credsId ? (
          <div data-tour={TOUR_ANCHORS.BUCKETS}>
            <S3Buckets credsId={credsId} tabs={tabs} />
          </div>
        ) : (
          <Loader text={"Loading data..."} />
        )
      ) : (
        <Fragment>
          <div data-tour={TOUR_ANCHORS.CREDENTIALS_TABLE}>
            <Table<TableCredentials>
              isSearchEnabled={true}
              isSortingEnabled={true}
              rows={credentialsList || []}
              columns={credentialsTableColumns}
              actions={credentialsTableActions}
              toolbarItems={
                <s.ToolbarContent>
                  <s.ToolbarButtons>
                    <Button
                      onClick={handleCreateCredentialsButtonClick}
                      variant={"contained"}
                      data-tour={TOUR_ANCHORS.CREATE_CREDENTIALS}
                    >
                      Create master key
                    </Button>
                  </s.ToolbarButtons>
                </s.ToolbarContent>
              }
              isLoading={!credentialsList}
              tabs={tabs}
            />
          </div>
          {/* Subusers belong to the credentials, so the block only makes sense
              once the credentials list has loaded and holds something. */}
          {credsId && (
            <Fragment>
              <s.TableSpacer />
              <div data-tour={TOUR_ANCHORS.SUB_USERS_TABLE}>
                <TableCollapsible<TableSubUser>
                  isSearchEnabled={true}
                  rows={subUsersList || []}
                  columns={subUsersTableColumns}
                  collapsibleDataKey={"keys"}
                  collapsibleDataColumns={subUserKeysTableColumns}
                  actions={subUsersTableActions}
                  collapsibleActions={subUserKeysTableActions}
                  isLoading={!subUsersList}
                  toolbarItems={
                    <s.ToolbarContent>
                      <s.ToolbarButtons>
                        <Button
                          onClick={handleCreateSubUserButtonClick}
                          variant={"contained"}
                          data-tour={TOUR_ANCHORS.CREATE_SUB_USER}
                        >
                          Create subuser
                        </Button>
                      </s.ToolbarButtons>
                    </s.ToolbarContent>
                  }
                />
              </div>
            </Fragment>
          )}
        </Fragment>
      )}
      <FormDialog
        isOpened={dialog.isOpened}
        onCancel={handleCloseDialog}
        fields={dialogProps[dialog.type].fields}
        onConfirm={dialogProps[dialog.type].onConfirm}
        title={dialogProps[dialog.type].title}
        confirmButtonLabel={dialogProps[dialog.type].confirmButtonLabel}
      />
      {credentials && isString(credentials.access_secret) && (
        <AccessSecretDialog
          isOpened={isAccessSecretDialogOpened}
          accessSecret={credentials.access_secret}
          onClose={handleCloseAccessSecretDialog}
        />
      )}
      {subUserKey && isString(subUserKey.secret_key) && (
        <AccessSecretDialog
          isOpened={isSubUserKeySecretDialogOpened}
          accessSecret={subUserKey.secret_key}
          onClose={handleCloseSubUserKeySecretDialog}
        />
      )}
    </>
  );
};
